// The build tag in the bar (docs/features/update-chip.md, Josh 10-10: "update buttons visible at all times, especially
// on facecheck / canary"). On facecheck, a branch preview and a local copy the bar always carries this page's build
// ("build 10-10 13:25"); a tap looks for a new version now; "Reload" (accent) when one is waiting — its tap takes it.
// On production only the existing "New version · Reload" bar (offline/view.js). Inside Cody's Android app the phone's
// own chip sits beside it (its canary or dev APK; the words come from the phone, buildtag/index.js).
//
// Plain functions, no DOM: `tagShown(host)`, `stampLabel(stamp)`, `tagView({stamp, phase})`, `phoneView(message)`,
// `narrowPick(tag, phone)`, `tagHTML(tag, phone)`.

const PRODUCTION = new Set(['codymccodeface.com', 'www.codymccodeface.com', 'app.codymccodeface.com'])
const NOT_APP = new Set(['admin.codymccodeface.com', 'updates.codymccodeface.com'])

/// Is the tag always there on this host? Facecheck, any other branch preview (`<branch>.codymccodeface.com`), a local
/// worker or a quick tunnel to one: yes. Production (and anything else, a Mac's own /xr/): no — the bar only.
export function tagShown(host = '') {
  host = String(host).toLowerCase()
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host.endsWith('.localhost') || host.endsWith('.trycloudflare.com')) return true
  return host.endsWith('.codymccodeface.com') && !PRODUCTION.has(host) && !NOT_APP.has(host)
}

const pad = (n) => String(n).padStart(2, '0')

/// "build 10-10 13:25" from the page's stamp (core/build-stamp.js readStamp), local time; "dev" for an unstamped page.
export function stampLabel({ builtAt = 0, commit = '' } = {}) {
  if (!builtAt) return commit ? 'build ' + commit.slice(0, 7) : 'dev'
  const d = new Date(builtAt)
  return `build ${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/// What the tag says and does. phase: 'idle' | 'checking' | 'current' (a look found nothing, for a moment) |
/// 'updating' (a new version is being fetched) | 'waiting' (installed, waiting for Reload).
export function tagView({ stamp = {}, phase = 'idle' } = {}) {
  switch (phase) {
    case 'waiting': return { text: 'Reload', accent: true, act: 'reload', title: 'A new version is ready · tap to reload' }
    case 'checking': return { text: 'Checking…', act: 'none', title: 'Looking for a new version' }
    case 'updating': return { text: 'Updating…', act: 'none', title: 'Fetching the new version' }
    case 'current': return { text: 'Up to date', act: 'check', title: 'This is the newest build · tap to check again' }
    default: return { text: stampLabel(stamp), act: 'check', idle: true, title: 'This page’s build · tap to check for a new version' }
  }
}

/// The phone app's chip from its bridge message (`{shown, text, accent, warn, busy}`, BuildChip.json in Kotlin); null
/// when it is not shown (a stable or a Play copy) or the message is not one.
export function phoneView(message) {
  let m = message
  if (typeof m === 'string') { try { m = JSON.parse(m) } catch { return null } }
  if (!m || m.shown !== true || typeof m.text !== 'string' || !m.text) return null
  return { text: m.text.slice(0, 40), accent: !!m.accent, warn: !!m.warn, act: m.busy ? 'none' : 'phone', title: m.accent ? 'Install the phone app’s update' : 'The phone app’s build · tap to check for an update' }
}

/// The words a phone's bar has room for: a build's time ("13:25"), a percent, one word.
export function shortText(text = '') {
  const t = String(text)
  const time = t.match(/^(?:build |canary · |dev · )(?:\d\d-\d\d )?(\d\d:\d\d)$/)
  if (time) return time[1]
  const pct = t.match(/^Updating · (\d+%)$/)
  if (pct) return pct[1]
  if (/^Updating/.test(t)) return 'Updating'
  if (/^Update · /.test(t)) return 'Waiting'
  if (t === 'Update failed') return 'Failed'
  if (t === 'Up to date') return 'Latest'
  return t
}

/// A phone's bar (under 600 px) has room for one small pill: the phone app's news first (Update, Updating, Failed),
/// then this page's (Reload, Checking…), then the phone app's build, then this page's — in its short words.
export function narrowPick(tag, phone) {
  const short = (v) => v && { ...v, text: shortText(v.text) }
  if (phone && (phone.accent || phone.warn || phone.act === 'none')) return [null, short(phone)]
  if (tag && !tag.idle) return [short(tag), null]
  if (phone) return [null, short(phone)]
  return [short(tag), null]
}

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
const pill = (v, id) => `<button type="button" id="${id}" class="pill${v.accent ? ' accent' : ''}${v.warn ? ' warn' : ''}" data-act="${esc(v.act)}" title="${esc(v.title)}"${v.act === 'none' ? ' aria-busy="true"' : ''}>${esc(v.text)}</button>`

/// The tags' HTML: this page's (when shown here) and the phone app's (inside the phone app, on its canary or dev copy).
export function tagHTML(tag, phone) {
  return (tag ? pill(tag, 'build-tag') : '') + (phone ? pill(phone, 'phone-build') : '')
}
